import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable} from 'rxjs';

import {FeatureFlagsEnum} from '../enums/feature-flags.enum';
import {FeatureFlagService} from '../services/feature-flag/feature-flag.service';

/**
 * A guard that checks if a specific feature flag is enabled for the current user.
 * The feature flag to check is specified in the route's data property under the key 'featureFlag'.
 *
 * @example
 * ```
 * const routes: Routes = [
 *   {
 *     path: 'new-feature',
 *     component: NewFeatureComponent,
 *     canActivate: [FeatureFlagGuard],
 *     data: { featureFlag: 'newAwesomeFeature', shouldEnable: (a) => !!a}
 *   }
 * ];
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class FeatureFlagGuardFactory {
    static create<T>(
        featureFlag: FeatureFlagsEnum,
        shouldEnable: (flagValue: T) => boolean,
        redirectTo?: string | string[]
    ): CanActivateFn {
        return (
            route: ActivatedRouteSnapshot,
            state: RouterStateSnapshot
        ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
            const featureFlagService = inject(FeatureFlagService);
            const flagValue = featureFlagService.getFeatureFlag<T>(featureFlag);
            if (shouldEnable(flagValue)) {
                return true;
            }

            if (redirectTo !== undefined) {
                return inject(Router).createUrlTree(Array.isArray(redirectTo) ? redirectTo : [redirectTo]);
            }

            return false;
        };
    }
}
